/**
 * <product-drawer> — a slide-in panel opened by links elsewhere on the page.
 *
 * The panel markup is rendered server-side by the block that owns it; this
 * element only wires up open/close, focus handling and the optional sticky
 * section nav. Trigger links are matched by the `trigger` attribute, e.g.
 * `trigger="#build-specs"` binds every <a href="#build-specs"> on the page.
 *
 * When the owning block has no data it renders nothing, so any trigger that
 * would open an empty panel is hidden by `hideOrphanTriggers`.
 */
class ProductDrawer extends HTMLElement {
  #controller;
  #lastFocused = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Move to <body> so the panel is never trapped by a transformed or
    // overflow-hidden ancestor in the product layout.
    if (this.parentElement !== document.body) document.body.appendChild(this);

    const selector = this.getAttribute('trigger');
    if (selector) {
      for (const link of document.querySelectorAll(`a[href="${selector}"]`)) {
        link.addEventListener('click', (event) => { event.preventDefault(); this.open(); }, { signal });
      }
    }

    this.querySelector('[data-drawer-close]')?.addEventListener('click', () => this.close(), { signal });
    this.querySelector('[data-drawer-backdrop]')?.addEventListener('click', () => this.close(), { signal });

    document.addEventListener(
      'keydown',
      (event) => { if (event.key === 'Escape' && !this.hidden) this.close(); },
      { signal }
    );

    this.#initNav(signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    document.body.style.overflow = '';
  }

  open() {
    this.#lastFocused = document.activeElement;
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-drawer-close]')?.focus({ preventScroll: true });
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(() => {
      this.hidden = true;
      // preventScroll: focus alone would scroll the trigger back into view,
      // jumping the reader away from wherever they had scrolled to.
      if (this.#lastFocused instanceof HTMLElement) this.#lastFocused.focus({ preventScroll: true });
    }, 300);
  }

  /** Sticky nav: highlight and scroll to the matching group. */
  #initNav(signal) {
    const nav = this.querySelector('[data-drawer-nav]');
    const scroller = this.querySelector('[data-drawer-panel]');
    if (!nav || !scroller) return;

    nav.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('button[data-target]');
        if (!button) return;
        for (const other of nav.querySelectorAll('button')) {
          other.classList.toggle('is-active', other === button);
        }
        const target = this.querySelector(`#${CSS.escape(button.dataset.target)}`);
        if (target) scroller.scrollTo({ top: target.offsetTop - 20, behavior: 'smooth' });
      },
      { signal }
    );
  }
}

if (!customElements.get('product-drawer')) {
  customElements.define('product-drawer', ProductDrawer);
}

/**
 * Hide trigger links whose drawer was not rendered (product has no data for it),
 * so the PDP never shows a link that opens nothing.
 *
 * A panel claims an anchor either by being a <product-drawer trigger="#…"> or by
 * carrying `data-drawer-target="#…"` (comma-separated for several). The second
 * form exists for the Santa Cruz template, whose drawers are still custom-Liquid
 * overlays rather than blocks — without it this would hide their triggers too.
 */
const DRAWER_ANCHORS = ['#build-specs', '#bike-geometry', '#setup-support'];

function hideOrphanTriggers() {
  const drawers = new Set(
    [...document.querySelectorAll('product-drawer[trigger]')].map((el) => el.getAttribute('trigger'))
  );

  for (const panel of document.querySelectorAll('[data-drawer-target]')) {
    for (const anchor of panel.dataset.drawerTarget.split(',')) {
      const trimmed = anchor.trim();
      if (trimmed) drawers.add(trimmed);
    }
  }

  const selector = [...DRAWER_ANCHORS.map((a) => `a[href="${a}"]`), 'a[href^="#"][data-drawer-trigger]'].join(',');

  for (const link of document.querySelectorAll(selector)) {
    if (drawers.has(link.getAttribute('href'))) continue;
    const container = link.classList.contains('card__link') ? link.closest('.card') : link.closest('p') || link;
    if (container instanceof HTMLElement) container.style.display = 'none';
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hideOrphanTriggers);
} else {
  hideOrphanTriggers();
}
